import { MutationResult, useApolloClient, useMutation } from '@apollo/client';
import { ExecutionResult } from 'graphql';

import {
  CreateTimelineEntryInput,
  CreateTimelineEntryMutation,
  CreateTimelineEntryMutationVariables,
  ImageMediaInput,
  ListTimelineEntriesQuery,
  ListTimelineEntriesQueryVariables,
  MeasurementSystem,
  PageType,
  TimelineEntryConnection,
  VideoMediaInput,
} from '../../../types/__generated__';
import { TimelineImage } from '../../components/Timeline/timelineTypes';
import { useAuthenticateUser } from '../useAuthenticateUser';
import { usePageMeta } from '../usePageMeta';
import mutation from './queries/createTimelineEntry.gql';
import query from './queries/listTimelineEntries.gql';
import { updateActivityTotals } from './updateActivityTotals';
import { useMeasurementSystem } from './useMeasurementSystem';

type CreateTimelineEntry = (
  entry: Pick<
    CreateTimelineEntryInput,
    'type' | 'pageId' | 'pageSlug' | 'message' | 'createdDate' | 'fitness'
  > & {
    media: TimelineImage[];
  },
) => Promise<ExecutionResult<CreateTimelineEntryMutation>>;

export function useCreateTimelineEntry(): [
  CreateTimelineEntry,
  MutationResult<CreateTimelineEntryMutation>,
] {
  const measurementSystem = useMeasurementSystem();
  const client = useApolloClient();
  const { pageType, pageSlug, isPreview, basePageData } = usePageMeta();
  const { accessToken } = useAuthenticateUser();
  const isSitebuilder =
    basePageData?.relationships?.campaigns?.nodes[0]?.isSitebuilder;

  const [runMutation, mutationResult] = useMutation<
    CreateTimelineEntryMutation,
    CreateTimelineEntryMutationVariables & {
      measurementSystem?: MeasurementSystem;
    }
  >(mutation, {
    update(cache, { data }) {
      if (!data || !data.createTimelineEntry || !pageSlug) {
        return;
      }

      const newTimelineEntry = {
        ...data.createTimelineEntry,
        refreshRequestedTime: null,
      };

      cache.updateQuery<
        ListTimelineEntriesQuery,
        ListTimelineEntriesQueryVariables
      >(
        {
          query,
          variables: {
            type: pageType.toUpperCase() as PageType,
            slug: pageSlug,
            measurementSystem,
          },
        },
        data => {
          const page = data?.page ?? getEmptyPage(pageSlug, pageType);
          const timeline =
            data?.page?.timeline ?? getEmptyTimelineEntryConnection();

          return {
            page: {
              ...page,
              timeline: {
                ...timeline,
                edges: [],
                nodes: [newTimelineEntry, ...timeline.nodes],
              },
            },
          };
        },
      );

      updateActivityTotals(client, pageType, pageSlug, isPreview);
    },
    context: {
      credentials: 'include',
    },
  });

  const createTimelineEntry: CreateTimelineEntry = entry => {
    return runMutation({
      variables: {
        input: {
          ...entry,

          ...(isSitebuilder && {
            creatorGuid: accessToken?.guid,
          }),
          media: entry?.media?.reduce<{
            imageMedia: ImageMediaInput[];
            videoMedia: VideoMediaInput[];
          }>(
            (acc, media) => {
              if (media.__typename === 'ImageMedia') {
                acc.imageMedia = [{ url: media.url }, ...acc.imageMedia];
              }
              return acc;
            },
            { imageMedia: [], videoMedia: [] },
          ),
        },
        measurementSystem: measurementSystem,
      },
    });
  };

  return [createTimelineEntry, mutationResult];
}

const getEmptyPage = (slug: string, type: string) => ({
  id: '',
  legacyId: '',
  type: type.toUpperCase() as PageType,
  slug,
  url: '',
  title: '',
});

function getEmptyTimelineEntryConnection(): TimelineEntryConnection {
  return {
    edges: [],
    nodes: [],
    pageInfo: {
      hasNextPage: false,
      hasPreviousPage: false,
      startCursor: '',
      endCursor: '',
    },
    totalCount: 0,
  };
}
